/**
 * LiveKit Room Connection Hook
 * Handles real-time video/audio streaming for AI interviews
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';

export const useLiveKit = () => {
  const [room, setRoom] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [remoteVideoTrack, setRemoteVideoTrack] = useState(null);
  const [remoteAudioTrack, setRemoteAudioTrack] = useState(null);
  const [agentReady, setAgentReady] = useState(false);
  const [avatarVideoReady, setAvatarVideoReady] = useState(false);
  const [dataMessages, setDataMessages] = useState([]);
  const [interviewClosing, setInterviewClosing] = useState(false);
  const [interviewComplete, setInterviewComplete] = useState(false);
  const [avatarSpeaking, setAvatarSpeaking] = useState(false);
  const [agentState, setAgentState] = useState(null); // Track agent state: ready, greeting, asking, listening, closing, finished

  const roomRef = useRef(null);
  const avatarStoppedSpeakingResolveRef = useRef(null); // For waitForAvatarToStopSpeaking promise
  const agentReadyResolveRef = useRef(null);
  const avatarVideoResolveRef = useRef(null); // For waitForAvatarVideo promise
  const interviewCompleteResolveRef = useRef(null); // For waitForInterviewComplete promise
  const audioContainerRef = useRef(null); // Container for audio elements

  /**
   * Connect to LiveKit room
   * @param {string} url - LiveKit WebSocket URL
   * @param {string} token - LiveKit access token
   * @param {Object} options - Connection options
   */
  const connect = useCallback(async (url, token, options = {}) => {
    setIsConnecting(true);
    setError(null);

    try {
      console.log('LiveKit: Connecting to room:', { url, hasToken: !!token });

      // Create new room instance
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: {
            width: 1280,
            height: 720,
            frameRate: 30,
          },
        },
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        // Connection stability options
        reconnectPolicy: {
          maxRetries: 5,
          retryDelayMs: 2000,
        },
        ...options,
      });

      // Set up event listeners
      setupRoomListeners(newRoom);

      // Connect to room
      console.log('LiveKit: Attempting connection to:', url);
      console.log('LiveKit: Token length:', token?.length, 'chars');
      await newRoom.connect(url, token);

      console.log('LiveKit: Connected successfully to room:', newRoom.name);
      console.log('LiveKit: Room state:', newRoom.state);
      console.log('LiveKit: Local participant:', newRoom.localParticipant?.identity);

      // Don't enable camera/mic yet - do it after backend interview starts
      // This matches the reference implementation and prevents connection issues

      setRoom(newRoom);
      setIsConnected(true);
      roomRef.current = newRoom;

      console.log('LiveKit: Successfully connected to room');
    } catch (err) {
      console.error('LiveKit: Failed to connect:', err);
      console.error('LiveKit: Error details:', {
        name: err.name,
        message: err.message,
        stack: err.stack
      });

      const errorMessage = err.message || 'فشل في الاتصال بغرفة المقابلة';

      // Provide more specific error messages
      let userMessage = errorMessage;
      if (err.message?.includes('token')) {
        userMessage = 'رمز الدخول غير صالح. الرجاء المحاولة مرة أخرى.';
      } else if (err.message?.includes('network') || err.message?.includes('timeout')) {
        userMessage = 'فشل الاتصال. الرجاء التحقق من الإنترنت والمحاولة مرة أخرى.';
      } else if (err.message?.includes('permission')) {
        userMessage = 'يرجى السماح بالوصول للكاميرا والميكروفون.';
      } else if (err.message?.includes('could not establish pc connection')) {
        userMessage = 'فشل في إنشاء اتصال. سيتم إعادة المحاولة...';
        console.error('LiveKit: PC connection failed - this may indicate the agent is not ready yet');
      }

      setError(userMessage);
      throw new Error(userMessage);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  /**
   * Disconnect from LiveKit room
   */
  const disconnect = useCallback(async () => {
    if (roomRef.current) {
      console.log('Disconnecting from LiveKit room');

      const localParticipant = roomRef.current.localParticipant;

      // First, explicitly disable camera and microphone to release hardware
      if (localParticipant) {
        try {
          console.log('LiveKit: Disabling microphone...');
          await localParticipant.setMicrophoneEnabled(false);
        } catch (e) {
          console.warn('LiveKit: Error disabling microphone:', e);
        }

        try {
          console.log('LiveKit: Disabling camera...');
          await localParticipant.setCameraEnabled(false);
        } catch (e) {
          console.warn('LiveKit: Error disabling camera:', e);
        }
      }

      // Stop all local tracks
      try {
        if (localParticipant) {
          // Stop and unpublish all local tracks
          for (const publication of localParticipant.trackPublications.values()) {
            if (publication.track) {
              console.log('LiveKit: Stopping local track:', publication.track.kind);
              publication.track.stop();
              try {
                await localParticipant.unpublishTrack(publication.track);
              } catch (e) {
                console.warn('LiveKit: Error unpublishing track:', e);
              }
            }
          }

          // Also iterate audioTrackPublications and videoTrackPublications
          for (const pub of localParticipant.audioTrackPublications.values()) {
            if (pub.track) {
              console.log('LiveKit: Stopping audio track');
              pub.track.stop();
            }
          }
          for (const pub of localParticipant.videoTrackPublications.values()) {
            if (pub.track) {
              console.log('LiveKit: Stopping video track');
              pub.track.stop();
            }
          }
        }
      } catch (err) {
        console.warn('LiveKit: Error stopping local tracks:', err);
      }

      // Clear audio elements BEFORE disconnecting
      if (audioContainerRef.current) {
        const audioElements = audioContainerRef.current.querySelectorAll('audio');
        audioElements.forEach(el => {
          if (el.srcObject) {
            el.srcObject.getTracks().forEach(track => {
              track.stop();
              console.log('LiveKit: Stopped audio element track');
            });
            el.srcObject = null;
          }
        });
        audioContainerRef.current.innerHTML = '';
        console.log('LiveKit: Audio container cleared');
      }

      await roomRef.current.disconnect();
      roomRef.current = null;
      setRoom(null);
      setIsConnected(false);
      setLocalVideoTrack(null);
      setLocalAudioTrack(null);
      setRemoteVideoTrack(null);
      setRemoteAudioTrack(null);
      setParticipants([]);
      setAgentReady(false);
      setAvatarVideoReady(false);
      setDataMessages([]);
      setInterviewClosing(false);
      setInterviewComplete(false);
      setAvatarSpeaking(false);
      setAgentState(null);
      agentReadyResolveRef.current = null;
      avatarVideoResolveRef.current = null;
      interviewCompleteResolveRef.current = null;
      avatarStoppedSpeakingResolveRef.current = null;

      console.log('LiveKit: Disconnected and all tracks stopped');
    }
  }, []);

  /**
   * Toggle local camera
   */
  const toggleCamera = useCallback(async () => {
    if (!roomRef.current) return;

    const enabled = roomRef.current.localParticipant.isCameraEnabled;
    await roomRef.current.localParticipant.setCameraEnabled(!enabled);

    return !enabled;
  }, []);

  /**
   * Toggle local microphone
   */
  const toggleMicrophone = useCallback(async () => {
    if (!roomRef.current) return;

    const enabled = roomRef.current.localParticipant.isMicrophoneEnabled;
    await roomRef.current.localParticipant.setMicrophoneEnabled(!enabled);

    return !enabled;
  }, []);

  /**
   * Send data message to room
   * @param {Object} message - Message object to send
   */
  const sendDataMessage = useCallback(async (message) => {
    if (!roomRef.current) {
      console.error('Cannot send data: room not connected');
      return false;
    }

    // Check room state
    if (roomRef.current.state !== 'connected') {
      console.error('Cannot send data: room state is', roomRef.current.state);
      return false;
    }

    try {
      const messageStr = JSON.stringify(message);
      const encoder = new TextEncoder();
      const data = encoder.encode(messageStr);

      console.log('LiveKit: Sending data message:', message);
      await roomRef.current.localParticipant.publishData(data, { reliable: true });
      console.log('LiveKit: Data message sent successfully');
      return true;
    } catch (err) {
      console.error('LiveKit: Failed to send data message:', err);
      return false;
    }
  }, []);

  /**
   * Wait for agent ready signal
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {Promise<boolean>} True if agent signaled ready, false if timeout
   */
  const waitForAgentReady = useCallback((timeoutMs = 5000) => {
    return new Promise((resolve) => {
      // Already ready
      if (agentReady) {
        console.log('LiveKit: Agent already ready');
        resolve(true);
        return;
      }

      // Store resolve function
      agentReadyResolveRef.current = resolve;

      // Timeout fallback
      const timeoutId = setTimeout(() => {
        console.warn('LiveKit: Agent ready timeout - proceeding anyway');
        agentReadyResolveRef.current = null;
        resolve(false);
      }, timeoutMs);

      // Clear timeout if resolved
      agentReadyResolveRef.current = (ready) => {
        clearTimeout(timeoutId);
        resolve(ready);
      };
    });
  }, [agentReady]);

  /**
   * Wait for avatar video track to be ready
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {Promise<boolean>} True if avatar video ready, false if timeout
   */
  const waitForAvatarVideo = useCallback((timeoutMs = 15000) => {
    return new Promise((resolve) => {
      // Already ready
      if (avatarVideoReady) {
        console.log('LiveKit: Avatar video already ready');
        resolve(true);
        return;
      }

      console.log('LiveKit: Waiting for avatar video track...');

      // Store resolve function
      avatarVideoResolveRef.current = resolve;

      // Timeout fallback
      const timeoutId = setTimeout(() => {
        console.warn('LiveKit: Avatar video timeout - proceeding without avatar');
        avatarVideoResolveRef.current = null;
        resolve(false);
      }, timeoutMs);

      // Clear timeout if resolved
      avatarVideoResolveRef.current = (ready) => {
        clearTimeout(timeoutId);
        resolve(ready);
      };
    });
  }, [avatarVideoReady]);

  /**
   * Wait for interview complete signal (after agent finishes closing statement)
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {Promise<boolean>} True if interview complete, false if timeout
   */
  const waitForInterviewComplete = useCallback((timeoutMs = 30000) => {
    return new Promise((resolve) => {
      // Already complete
      if (interviewComplete) {
        console.log('LiveKit: Interview already complete');
        resolve(true);
        return;
      }

      console.log('LiveKit: Waiting for interview complete signal...');

      // Store resolve function
      interviewCompleteResolveRef.current = resolve;

      // Timeout fallback
      const timeoutId = setTimeout(() => {
        console.warn('LiveKit: Interview complete timeout - proceeding anyway');
        interviewCompleteResolveRef.current = null;
        resolve(false);
      }, timeoutMs);

      // Clear timeout if resolved
      interviewCompleteResolveRef.current = (ready) => {
        clearTimeout(timeoutId);
        resolve(ready);
      };
    });
  }, [interviewComplete]);

  /**
   * Wait for avatar to stop speaking (used after sending end_interview)
   * @param {number} timeoutMs - Timeout in milliseconds
   * @param {number} silenceMs - How long avatar must be silent before resolving
   * @returns {Promise<boolean>} True if avatar stopped speaking, false if timeout
   */
  const waitForAvatarToStopSpeaking = useCallback((timeoutMs = 30000, silenceMs = 2000) => {
    return new Promise((resolve) => {
      console.log('LiveKit: Waiting for avatar to stop speaking...');

      let silenceTimer = null;

      // Function to check silence
      const checkSilence = () => {
        // Clear any existing silence timer
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }

        // If not speaking, start silence countdown
        silenceTimer = setTimeout(() => {
          console.log('LiveKit: Avatar silent for', silenceMs, 'ms - resolving');
          cleanup();
          resolve(true);
        }, silenceMs);
      };

      // Store resolve function for ActiveSpeakersChanged event
      avatarStoppedSpeakingResolveRef.current = () => {
        // Avatar stopped - start silence countdown
        checkSilence();
      };

      // Timeout fallback
      const timeoutId = setTimeout(() => {
        console.warn('LiveKit: Avatar stop speaking timeout - proceeding anyway');
        cleanup();
        resolve(false);
      }, timeoutMs);

      // Cleanup function
      const cleanup = () => {
        clearTimeout(timeoutId);
        if (silenceTimer) clearTimeout(silenceTimer);
        avatarStoppedSpeakingResolveRef.current = null;
      };

      // Start initial check (in case avatar is already not speaking)
      checkSilence();
    });
  }, []);

  /**
   * Enable microphone (call after backend interview starts)
   */
  const enableMicrophone = useCallback(async () => {
    if (!roomRef.current) {
      console.error('Cannot enable microphone: room not connected');
      return false;
    }

    try {
      console.log('LiveKit: Enabling microphone...');
      await roomRef.current.localParticipant.setMicrophoneEnabled(true);

      // Get audio track
      const audioTrack = roomRef.current.localParticipant.audioTrackPublications.values().next().value?.track;
      setLocalAudioTrack(audioTrack);

      console.log('LiveKit: Microphone enabled');
      return true;
    } catch (err) {
      console.error('LiveKit: Failed to enable microphone:', err);
      return false;
    }
  }, []);

  /**
   * Send start interview signal to agent
   */
  const startInterviewSignal = useCallback(async () => {
    console.log('LiveKit: Sending start_interview signal to agent...');
    const success = await sendDataMessage({ type: 'start_interview' });
    if (success) {
      console.log('LiveKit: start_interview signal sent successfully');
    } else {
      console.error('LiveKit: Failed to send start_interview signal');
    }
    return success;
  }, [sendDataMessage]);

  /**
   * Check if room is currently connected
   */
  const isRoomConnected = useCallback(() => {
    return roomRef.current && roomRef.current.state === 'connected';
  }, []);

  /**
   * Set up room event listeners
   */
  const setupRoomListeners = (room) => {
    // Participant connected
    room.on(RoomEvent.ParticipantConnected, (participant) => {
      console.log('Participant connected:', participant.identity);
      setParticipants((prev) => [...prev, participant]);
    });

    // Participant disconnected
    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      console.log('Participant disconnected:', participant.identity);
      setParticipants((prev) =>
        prev.filter((p) => p.identity !== participant.identity)
      );
    });

    // Track subscribed (remote participant's track)
    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      console.log('Track subscribed:', {
        kind: track.kind,
        participant: participant.identity,
      });

      if (track.kind === Track.Kind.Video) {
        setRemoteVideoTrack(track);

        // Check if this is the avatar video track
        if (participant.identity.includes('anam') || participant.identity.includes('avatar')) {
          console.log('LiveKit: Avatar video track ready!');
          setAvatarVideoReady(true);

          // Resolve waiting promise if any
          if (avatarVideoResolveRef.current) {
            avatarVideoResolveRef.current(true);
            avatarVideoResolveRef.current = null;
          }
        }
      } else if (track.kind === Track.Kind.Audio) {
        // IMMEDIATELY attach audio to prevent losing audio data
        // Don't wait for React state cycle
        if (audioContainerRef.current) {
          const audioEl = document.createElement('audio');
          audioEl.autoplay = true;
          audioEl.playsInline = true;
          audioEl.id = `livekit-audio-${participant.identity}`;
          audioContainerRef.current.appendChild(audioEl);
          track.attach(audioEl);
          console.log('LiveKit: Audio track IMMEDIATELY attached for:', participant.identity);
        }
        // Also update React state for reference
        setRemoteAudioTrack(track);
      }
    });

    // Track unsubscribed
    room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
      console.log('Track unsubscribed:', {
        kind: track.kind,
        participant: participant.identity,
      });

      if (track.kind === Track.Kind.Video) {
        setRemoteVideoTrack(null);
      } else if (track.kind === Track.Kind.Audio) {
        // Remove the audio element
        const audioEl = document.getElementById(`livekit-audio-${participant.identity}`);
        if (audioEl) {
          track.detach(audioEl);
          audioEl.remove();
          console.log('LiveKit: Audio element removed for:', participant.identity);
        }
        setRemoteAudioTrack(null);
      }
    });

    // Connection quality changed
    room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
      console.log('Connection quality changed:', {
        quality,
        participant: participant.identity,
      });
    });

    // Disconnected
    room.on(RoomEvent.Disconnected, (reason) => {
      console.log('LiveKit: Room disconnected. Reason:', reason, 'State:', room.state);
      setIsConnected(false);

      // Log disconnect reason code for debugging
      if (typeof reason === 'number') {
        console.log('LiveKit: Disconnect code:', reason);
        // Code 2 = DUPLICATE_IDENTITY or similar - may indicate token/auth issue
      }

      // Don't set error for normal disconnections (user initiated)
      if (reason && reason !== 'CLIENT_INITIATED') {
        console.error('LiveKit: Unexpected disconnection:', reason);
        setError(reason ? `تم قطع الاتصال: ${reason}` : 'تم قطع الاتصال');
      }
    });

    // Reconnecting
    room.on(RoomEvent.Reconnecting, () => {
      console.log('Reconnecting to room...');
      setError('جاري إعادة الاتصال...');
    });

    // Reconnected
    room.on(RoomEvent.Reconnected, () => {
      console.log('Reconnected to room');
      setError(null);
    });

    // Active speakers changed - detect when avatar is speaking
    room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
      // Check if avatar/agent is among active speakers
      const avatarIsSpeaking = speakers.some(
        (speaker) =>
          speaker.identity.includes('anam') ||
          speaker.identity.includes('avatar') ||
          speaker.identity.includes('agent')
      );

      console.log('LiveKit: Active speakers changed, avatar speaking:', avatarIsSpeaking);
      setAvatarSpeaking(avatarIsSpeaking);

      // If avatar stopped speaking and we're waiting for it
      if (!avatarIsSpeaking && avatarStoppedSpeakingResolveRef.current) {
        console.log('LiveKit: Avatar stopped speaking - resolving wait promise');
        avatarStoppedSpeakingResolveRef.current(true);
        avatarStoppedSpeakingResolveRef.current = null;
      }
    });

    // Data received (agent messages)
    room.on(RoomEvent.DataReceived, (data, participant) => {
      try {
        const decoder = new TextDecoder();
        const messageStr = decoder.decode(data);
        const message = JSON.parse(messageStr);

        console.log('LiveKit: Data received from', participant?.identity || 'server', ':', message);

        // Store message
        setDataMessages((prev) => [...prev, message]);

        // Handle agent ready signal
        if (message.type === 'agent_ready') {
          console.log('LiveKit: Agent ready signal received');
          setAgentReady(true);

          // Resolve waiting promise if any
          if (agentReadyResolveRef.current) {
            agentReadyResolveRef.current(true);
            agentReadyResolveRef.current = null;
          }
        }

        // Handle interview closing signal (avatar starting goodbye)
        if (message.type === 'interview_closing') {
          console.log('LiveKit: Interview closing - avatar saying goodbye');
          setInterviewClosing(true);
        }

        // Handle interview complete signal (avatar done speaking)
        if (message.type === 'interview_complete') {
          console.log('LiveKit: Interview complete signal received');
          setInterviewComplete(true);

          // Resolve waiting promise if any
          if (interviewCompleteResolveRef.current) {
            interviewCompleteResolveRef.current(true);
            interviewCompleteResolveRef.current = null;
          }
        }

        // Parse agent_log messages for state transitions
        // Format: "[timestamp] 🤖 AGENT: 📍 STATE: oldState -> newState"
        if (message.type === 'agent_log' && message.message) {
          const stateMatch = message.message.match(/📍 STATE: \w+ -> (\w+)/);
          if (stateMatch) {
            const newState = stateMatch[1];
            console.log('LiveKit: Agent state changed to:', newState);
            setAgentState(newState);

            // When state becomes "closing", interview is ending
            if (newState === 'closing') {
              console.log('LiveKit: Agent entering closing state');
              setInterviewClosing(true);
            }

            // When state becomes "finished", closing statement is complete
            if (newState === 'finished') {
              console.log('LiveKit: Agent finished - interview complete');
              setInterviewComplete(true);

              // Resolve waiting promise if any
              if (interviewCompleteResolveRef.current) {
                interviewCompleteResolveRef.current(true);
                interviewCompleteResolveRef.current = null;
              }
            }
          }
        }
      } catch (err) {
        console.error('LiveKit: Failed to parse data message:', err);
      }
    });
  };

  // Create audio container on mount for immediate audio attachment
  useEffect(() => {
    // Create a hidden container for audio elements
    const container = document.createElement('div');
    container.id = 'livekit-audio-container';
    container.style.display = 'none';
    document.body.appendChild(container);
    audioContainerRef.current = container;
    console.log('LiveKit: Audio container created');

    return () => {
      // Cleanup audio container
      if (audioContainerRef.current) {
        audioContainerRef.current.remove();
        audioContainerRef.current = null;
        console.log('LiveKit: Audio container removed');
      }
      if (roomRef.current) {
        roomRef.current.disconnect();
      }
    };
  }, []);

  // Auto-mute microphone when avatar is speaking to prevent interruptions
  useEffect(() => {
    if (!roomRef.current || !isConnected) return;

    const muteWhileAvatarSpeaks = async () => {
      try {
        if (avatarSpeaking) {
          // Mute when avatar starts speaking
          console.log('LiveKit: Avatar speaking - muting microphone');
          await roomRef.current.localParticipant.setMicrophoneEnabled(false);
        } else {
          // Unmute when avatar stops speaking (if interview is still active)
          // Don't rely solely on agentState to avoid missing unmutes due to:
          // - State update delays
          // - Lost data channel messages
          // - Agent disconnections
          // Only keep muted if interview has explicitly ended
          const shouldUnmute = !interviewComplete && !interviewClosing;

          if (shouldUnmute) {
            console.log('LiveKit: Avatar stopped speaking - unmuting microphone', {
              agentState,
              interviewComplete,
              interviewClosing
            });
            await roomRef.current.localParticipant.setMicrophoneEnabled(true);
          } else {
            console.log('LiveKit: Avatar stopped but interview ending - keeping muted', {
              interviewComplete,
              interviewClosing
            });
          }
        }
      } catch (err) {
        console.error('LiveKit: Failed to toggle microphone:', err);
      }
    };

    muteWhileAvatarSpeaks();
  }, [avatarSpeaking, agentState, isConnected, interviewComplete, interviewClosing]);

  return {
    room,
    isConnecting,
    isConnected,
    error,
    participants,
    localVideoTrack,
    localAudioTrack,
    remoteVideoTrack,
    remoteAudioTrack,
    agentReady,
    avatarVideoReady,
    dataMessages,
    interviewClosing,
    interviewComplete,
    avatarSpeaking,
    agentState,
    connect,
    disconnect,
    toggleCamera,
    toggleMicrophone,
    enableMicrophone,
    sendDataMessage,
    waitForAgentReady,
    waitForAvatarVideo,
    waitForInterviewComplete,
    waitForAvatarToStopSpeaking,
    startInterviewSignal,
    isRoomConnected,
  };
};

export default useLiveKit;
